import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useLogDetail } from '@/api/hooks/useLogs';
import { ApiException } from '@/api/client';

export function LogDetailPage() {
  const { request_id = '' } = useParams<{ request_id: string }>();
  const decodedId = decodeURIComponent(request_id);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Pass the caller's current date range so we can find logs from prior days.
  // The list page links to detail with ?start_date=&end_date= so historical
  // logs are still resolvable; defaults to today UTC on the worker otherwise.
  const range = {
    start_date: searchParams.get('start_date') ?? undefined,
    end_date: searchParams.get('end_date') ?? undefined,
  };
  const { data, isLoading, error } = useLogDetail(decodedId, range);

  if (isLoading) {
    return (
      <div
        style={{
          height: 384,
          background: '#e2e8f0',
          borderRadius: 8,
          animation: 'pulse 2s infinite',
        }}
      />
    );
  }

  if (error || !data) {
    return (
      <Card>
        <div style={{ padding: 24, color: '#991b1b', fontSize: 13 }}>
          <strong>Failed to load log:</strong>{' '}
          {error instanceof ApiException ? error.message : 'Not found'}
          <div style={{ marginTop: 12 }}>
            <Button variant="outline" onClick={() => navigate('/logs')}>
              Back to Logs
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const maskedApiKey = data.api_key
    ? `sk-...${String(data.api_key).slice(-8)}`
    : '-';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h1
        style={{
          fontSize: 18,
          fontWeight: 700,
          margin: 0,
          fontFamily: 'monospace',
          wordBreak: 'break-all',
        }}
      >
        {data.request_id}
      </h1>
      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Summary</h2>
        <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Row
            label="Time"
            value={data.timestamp ? new Date(data.timestamp).toLocaleString() : '-'}
          />
          <Row label="Model" value={data.model ?? '-'} />
          <Row label="User" value={data.user ?? '-'} />
          <Row label="Team" value={data.team_id ?? '-'} />
          <Row label="End User" value={data.end_user ?? '-'} />
          <Row
            label="Status"
            value={
              <span style={{ color: data.status === 'success' ? '#16a34a' : '#dc2626' }}>
                {data.status ?? '-'}
              </span>
            }
          />
          <Row
            label="Cost"
            value={typeof data.cost === 'number' ? `$${data.cost.toFixed(4)}` : '-'}
          />
          <Row
            label="Latency"
            value={data.latency_ms != null ? `${data.latency_ms}ms` : '-'}
          />
          <Row label="Total Tokens" value={data.total_tokens ?? '-'} />
          <Row label="Prompt Tokens" value={data.prompt_tokens ?? '-'} />
          <Row label="Completion Tokens" value={data.completion_tokens ?? '-'} />
          <Row
            label="API Key"
            value={<span style={{ fontFamily: 'monospace' }}>{maskedApiKey}</span>}
          />
          <Row label="IP Address" value={data.requester_ip_address ?? '-'} />
          <Row label="Call Type" value={data.call_type ?? '-'} />
        </dl>
      </Card>

      {data.messages != null && (
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Messages</h2>
          <JsonView value={data.messages} />
        </Card>
      )}

      {data.response != null && (
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Response</h2>
          <JsonView value={data.response} />
        </Card>
      )}

      {data.error_information != null && (
        <Card>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 600,
              margin: '0 0 12px',
              color: '#dc2626',
            }}
          >
            Error Information
          </h2>
          <JsonView value={data.error_information} />
        </Card>
      )}

      {data.metadata != null && (
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px' }}>Metadata</h2>
          <JsonView value={data.metadata} />
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: '#64748b', fontSize: 13 }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: 14, color: '#0f172a' }}>{value}</dd>
    </>
  );
}

function JsonView({ value }: { value: unknown }) {
  return (
    <pre
      style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        padding: 12,
        fontSize: 12,
        fontFamily: 'monospace',
        overflow: 'auto',
        maxHeight: 400,
        margin: 0,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}